'use client';

import type { ReactNode } from 'react';

import { motion, useReducedMotion } from 'motion/react';

/**
 * The whole scroll-reveal contract, in four values. A component that cannot use `Reveal` itself —
 * because the element it needs to animate is fixed by its parent's markup — composes these instead
 * of restating them, or the page ends up with several nearly-identical reveals.
 */
export const REVEAL_STEP = 0.11;
/** The site's other curve: for chrome that opens and closes rather than arriving on scroll. */
export const UI_EASE = [0.33, 0, 0.2, 1] as const;

export const REVEAL_EASE = [0.16, 1, 0.3, 1] as const;
export const REVEAL_DURATION = 0.45;
export const REVEAL_VIEWPORT = { once: true, amount: 0.4 } as const;

/**
 * How far a block travels on its way in. Far enough to read as arriving from somewhere rather than
 * as a fade with a wobble; short enough that a column of them doesn't look like it is falling.
 */
export const REVEAL_RISE = 26;
/** Sideways travel is longer — horizontal motion reads as slower at the same distance. */
export const REVEAL_SLIDE = 34;

/** Where a block comes in from. `up` is the default; the sides are for content that is already
 *  arranged in columns, where the two halves converging says they belong to each other. */
export type RevealFrom = 'up' | 'left' | 'right';

export function revealOffset(from: RevealFrom) {
  if (from === 'left') return { x: -REVEAL_SLIDE, y: 0 };
  if (from === 'right') return { x: REVEAL_SLIDE, y: 0 };
  return { x: 0, y: REVEAL_RISE };
}

/**
 * Fades a block up as it reaches the viewport.
 *
 * Each item triggers on its own rather than a parent staggering all of them at once: on a tall
 * screen a group runs 1-2-3-4 as intended, but on a short one the later items are still below the
 * fold when the first plays, and a container-level stagger would burn their turn unseen.
 */
export function Reveal({
  index = 0,
  as = 'div',
  from = 'up',
  id,
  tabIndex,
  className,
  children,
}: {
  /** Position in the cascade — only sets the delay, not the trigger. */
  index?: number;
  /** The element to render. A row of an `ol` has to stay an `li` to keep the list intact. */
  as?: 'div' | 'li';
  from?: RevealFrom;
  /** So a revealed row can also be an in-page anchor. */
  id?: string;
  /** `-1` makes a revealed card focusable by pointer — see `CARD_HOVER`. */
  tabIndex?: number;
  className?: string;
  children: ReactNode;
}) {
  const reducedMotion = useReducedMotion();
  const Element = as === 'li' ? motion.li : motion.div;

  return (
    <Element
      id={id}
      tabIndex={tabIndex}
      // The server renders this at opacity 0, so without JS the page below the hero would be
      // blank. The `noscript` rule in the layout keys off this attribute to put it all back.
      data-reveal=""
      className={className}
      initial={reducedMotion ? false : { opacity: 0, ...revealOffset(from) }}
      whileInView={{ opacity: 1, x: 0, y: 0 }}
      viewport={REVEAL_VIEWPORT}
      transition={{ duration: REVEAL_DURATION, ease: REVEAL_EASE, delay: index * REVEAL_STEP }}
    >
      {children}
    </Element>
  );
}
